/**
 * presenceStore — slim state store subscribing to PRESENCE_UPDATE.
 *
 * This store drives the connection pill and busy-state UI directly from
 * the server's PRESENCE_UPDATE events. Intentionally minimal — there is
 * no state machine on the client; presence is purely a mirror of the
 * server's authoritative observation.
 *
 * Spec: docs/superpowers/specs/2026-04-23-connection-state-ttft-architecture-design.md §3.1
 */

import { create } from "zustand";
import type { DisplayStatus, Presence } from "@bash/shared";
import { EVENTS } from "@/constants/events";
import { getSocket } from "./socketService";

interface PresenceStore {
  presence: Presence;
  updatePresence: (p: Presence) => void;
  isBusy: () => boolean;
  _subscribed: boolean;
  _subscribe: () => void;
  _unsubscribe: () => void;
}

export const usePresenceStore = create<PresenceStore>((set, get) => ({
  presence: { phase: "hibernating", activeTasks: 0 },
  updatePresence: (p: Presence) => set({ presence: p }),
  isBusy: () => {
    const p = get().presence;
    return p.phase === "ready" && p.activeTasks > 0;
  },
  _subscribed: false,
  _subscribe: () => {
    if (get()._subscribed) return;
    const socket = getSocket();
    if (!socket) return;
    socket.on(EVENTS.PRESENCE_UPDATE, (p: Presence) => set({ presence: p }));
    set({ _subscribed: true });
  },
  _unsubscribe: () => {
    const socket = getSocket();
    if (socket) socket.off(EVENTS.PRESENCE_UPDATE);
    set({ _subscribed: false });
  },
}));

/**
 * Phase 1 Task 6b — derive a single always-visible CSS color for the
 * connection-icon status dot in TaskHeaderBar. Unlike ConnectionPill (which
 * hides during the ready/hibernating steady states), this dot is always
 * visible so users can glance at their sandbox state at any time.
 *
 * Returns a CSS color string. Caller applies it as the background of a
 * small overlay dot inside the circular ConnectionIcon button.
 *
 * Spec: docs/superpowers/specs/2026-04-23-connection-state-ttft-architecture-design.md §3.1
 */
export function derivePresenceDotColor(presence: Presence): string {
  switch (presence.phase) {
    case "hibernating":
      return "var(--fg-muted)"; // subtle gray — sandbox asleep
    // 'reconnecting' (the reattach rail re-dialing a runner that is still
    // working) wears warming's amber for now — both are transient, and the UI
    // session owns the word this phase finally gets.
    case "warming":
    case "reconnecting":
      return "var(--color-amber)"; // amber — attention, transient
    case "ready":
      // Ready is the steady state. Busy (activeTasks > 0) uses the bright
      // green; idle uses the calm foreground color so both still show a dot.
      return presence.activeTasks > 0
        ? "var(--color-green)" // brighter green when busy
        : "var(--fg-default)"; // calm dark/light when idle
    case "upgrading":
      return "var(--color-cyan)"; // cyan — big event
    case "error":
      return "var(--color-red)"; // red — broken
    default: {
      // Exhaustiveness guard — adding a PresencePhase without handling it
      // fails the build here.
      const _exhaustive: never = presence.phase;
      throw new Error(`Unhandled presence phase: ${String(_exhaustive)}`);
    }
  }
}

/**
 * Phase 1 Task 6a shim — map Presence to the legacy DisplayStatus-shaped
 * view that connectionStore exposes today. Consumed by connectionStore
 * when the web flag is on, so all downstream consumers of
 * connectionState.status / activeTaskCount / activityReasons continue to
 * work without per-consumer migration. Will be retired in Phase 3 cleanup
 * when the legacy DisplayStatus enum is deleted.
 */
export function deriveLegacyStateFromPresence(
  presence: Presence,
  /** The browser pane's RESOLVED state (browserStore.viewSnapshot.state) —
   *  the machine's own truth, recomputed every beat from relay + creds +
   *  demand. `'live'` means pixels are streaming to a demanded pane. Callers
   *  pass it; the default keeps every legacy/test caller byte-identical. */
  paneState: "hidden" | "waking" | "live" | "suspended" | "error" = "hidden"
): {
  status: DisplayStatus;
  activeTaskCount: number;
  activityReasons: string[];
  error?: string;
} {
  const activeTasks = presence.activeTasks;
  // ONE QUESTION: is my computer awake?  (founder, 2026-08-24)
  //
  // This badge used to answer two questions at once — "is the computer awake"
  // AND "what exactly is it doing" — and the second one is what kept lying.
  // `browsing` in particular never meant "the agent is driving the browser"; it
  // meant "a pane is live", so it fired on a typing-prewarm re-serve with nobody
  // touching anything, and stuck on after the driving stopped. Every fix was
  // another attempt to keep a WORD in sync with something it wasn't derived
  // from.
  //
  // So the word is gone. Awake is `active`, whether that is an agent mid-turn,
  // the agent driving the browser, or the user watching a pane. What the agent
  // is *doing* is already narrated where it belongs — the task's own step list
  // — and when the browser is open the user is looking at the browser, so a
  // badge repeating "browsing" only ever got noticed when it was wrong.
  //
  // Two things make this honest rather than lossy:
  //   - the LIVENESS claim is untouched. An agent driving the browser still
  //     holds the sandbox awake; only its display word is folded away. Deleting
  //     the claim with the word would put the browser to sleep mid-browse.
  //   - awake ~= something is happening, structurally, now that an idle box
  //     stands down in ~7s.
  //
  // CORRECTED 2026-08-25 (founder: "how do we know when its idle?"). The first
  // pass folded resting `idle` into `active` as well, which was one question too
  // far. `browsing` deserved deleting because it was a WORD nobody kept in sync;
  // `idle` is a real distinction the payload computes on every beat, and the UI
  // asserts on it: TaskHeaderBar renders `active` as "Working" with the tooltip
  // "Agent is working on your task." So an awake, quiet sandbox claimed the
  // agent was working when it was not — the exact class of lie this whole
  // program exists to kill, reintroduced by the fix for it.
  //
  // So: ONE word for awake-and-working, ONE for awake-and-not, and no
  // `browsing`. `activeTasks` is the only input that separates them, and it is
  // read straight from the presence beat rather than from a marker.
  //
  // A real error still wins over everything: errors must never be hidden.
  if (presence.phase === "ready" && activeTasks > 0) {
    return {
      status: "active",
      activeTaskCount: activeTasks,
      activityReasons: ["task-active"],
    };
  }
  if (paneState === "live" && presence.phase !== "error") {
    // Pixels are streaming, but nobody is working — the agent claim is what
    // makes it `active`, and there is none. `idle` reads "Ready", which is true.
    return {
      status: "idle",
      activeTaskCount: 0,
      activityReasons: ["browser-open"],
    };
  }
  //   2b. A WAKING pane is the thing that is starting. A rotation wake walks
  //      the phase word honestly but noisily (ready for 0.6s before the L2
  //      fence, then warming again); the user asked for one word until the
  //      pixels land: `updating` while the spec converge itself runs (he wants
  //      to see that), `starting` otherwise. An error still wins.
  if (paneState === "waking" && presence.phase !== "error") {
    return {
      status: presence.phase === "upgrading" ? "updating" : "starting",
      activeTaskCount: 0,
      activityReasons: ["browser-open"],
    };
  }
  switch (presence.phase) {
    case "hibernating":
      // A watched hosted app means the sandbox is NOT standing by: an
      // apps-only wake never dials the runner, so the phase word stays
      // 'hibernating' while the app provably serves (the wire marker is
      // TTL-derived from the apps:view heartbeat). Same ruling as the
      // browser pane above: watching is awake-not-working, so the word is
      // `idle` ("Ready"), never `active` ("Working") — the badge stops
      // saying Standby under a live app without claiming the agent works.
      if (presence.hasAppActivity) {
        return {
          status: "idle",
          activeTaskCount: 0,
          activityReasons: ["app-open"],
        };
      }
      return { status: "offline", activeTaskCount: 0, activityReasons: [] };
    // The legacy DisplayStatus vocabulary has no word for a re-dial, so
    // 'reconnecting' rides warming's until the UI session gives it one.
    case "warming":
    case "reconnecting":
      return { status: "starting", activeTaskCount: 0, activityReasons: [] };
    case "upgrading":
      return { status: "updating", activeTaskCount: 0, activityReasons: [] };
    case "ready":
      // Work and pixels were handled above; this is the resting 'ready'.
      // "Browsing" means ONE thing — the pane is LIVE (checked above from the
      // machine's own resolved state, never a marker). The two lies that rule
      // replaces were both marker-driven: 'browsing' over a suspended pane
      // (the legacy marker outlived the stream — founder: "stuck at
      // browsing"), and 'idle' over a live one (the over-correction — founder:
      // "the browser opened, connection state shows idle, not browsing").
      // Open-but-not-live (waking / suspended / error pane) is the sandbox
      // idle with the pane's own card saying what the browser is doing; the
      // reason survives for surfaces that care a browser surface exists.
      // Resting `ready` — awake, billing, able to act instantly, and standing
      // down on its own in ~7s. Awake is not the same as WORKING, and the UI
      // says "Working" for `active`, so this is `idle` ("Ready").
      if (presence.hasBrowserSession) {
        return {
          status: "idle",
          activeTaskCount: 0,
          activityReasons: ["browser-open"],
        };
      }
      return { status: "idle", activeTaskCount: 0, activityReasons: [] };
    case "error":
      return {
        status: "error",
        activeTaskCount: 0,
        activityReasons: [],
        error: presence.error?.message,
      };
    default: {
      // Exhaustiveness guard — if PresencePhase gains a variant, this fails at build time.
      const _exhaustive: never = presence.phase;
      throw new Error(`Unhandled presence phase: ${String(_exhaustive)}`);
    }
  }
}
